import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type {
	AiStreamingOrchestratorPlugin,
	StreamingCommand,
	StreamingError,
} from '@atlassian/editor-plugin-ai-streaming-orchestrator';
import type { MultiFormatContentType } from '@atlassian/editor-plugin-ai-streaming-orchestrator/types';

import type { ContentLocation } from '../types/content';
import { log } from '../utils/aifc-logging';

import {
	getEnvelopeContentType,
	type ToolCallV2Envelope,
} from './partial-stream-parser-v2';
import {
	createToolProcessorChunkStrategyV2,
	type ToolProcessorChunkStrategyV2,
} from './tool-processor-chunk-strategies-v2';

/**
 * V2 per-tool-call processor for the HTML+ incremental streaming contract.
 *
 * Each {@link ToolCallV2Envelope} the parser emits for a given `id` is
 * routed to the same `ToolCallProcessorV2` instance, which translates the
 * envelope into orchestrator actions:
 *
 * - On the first envelope: build the {@link StreamingCommand} from the
 *   envelope's `name` + `arguments` and call `initAction`.
 * - On every envelope: pass `arguments.value` (the full snapshot) through the
 *   per-content-type V2 chunk strategy, then dispatch `replaceValue` to the
 *   orchestrator. This is the only place that calls `replaceValue` —
 *   `streamValue` (v1, append-style) is never used on the v2 path.
 * - On `status === 'complete'`: call `finishAction`.
 *
 * Errors building a command silently drop the envelope (matching v1
 * `createToolCallProcessor` behaviour).
 */
export class ToolCallProcessorV2 {
	private actionId: string | undefined;
	private command: StreamingCommand | undefined;
	private strategy: ToolProcessorChunkStrategyV2 | undefined;
	private finished: boolean = false;
	/**
	 * Set to `true` when `initAction` returns falsy. Once latched, subsequent
	 * `apply()` calls short-circuit so we don't repeatedly call `initAction`
	 * with a fresh UUID each chunk (which would silently leak per-chunk
	 * un-finishable commands into the `StreamCommandQueue` and block
	 * `onEmptyQueue()` indefinitely).
	 */
	private initFailed: boolean = false;

	constructor(
		private editorApi: PublicPluginAPI<AiStreamingOrchestratorPlugin>,
		private contentLocation: ContentLocation,
	) {}

	/**
	 * Apply one v2 envelope to this tool call. Idempotent against repeat /
	 * stale sequences — the parser is expected to filter those out before
	 * calling, but we still guard against `finished` here.
	 */
	apply(envelope: ToolCallV2Envelope): void {
		if (this.finished || this.initFailed) {
			return;
		}

		// Lazy init on first envelope so the command is built from the most
		// recent metadata available. After `initAction`, subsequent envelopes
		// only stream a new snapshot.
		if (!this.actionId) {
			const command = buildCommandFromEnvelope(envelope);
			if (!command) {
				// Couldn't build a valid command — refuse to init. Stays consistent
				// with v1's silent-drop behaviour for malformed tool calls. Latch
				// `initFailed` so subsequent envelopes for the same `id` don't
				// retry (and don't keep re-evaluating the malformed envelope).
				this.initFailed = true;
				log('V2: init failed (malformed envelope)', {
					streamingContract: 'v2',
					envelopeId: envelope.id,
				});
				return;
			}
			this.command = command;
			const actionId = this.editorApi?.aiStreamingOrchestrator?.actions.initAction(command, {
				hideLoadingScreen: this.contentLocation === 'staging-area',
			});
			if (!actionId) {
				// `initAction` returned falsy — latch `initFailed` so subsequent
				// `apply()` calls short-circuit instead of repeatedly calling
				// `initAction` with a fresh UUID. Without this, every chunk for
				// the same envelope id would queue a new command that never
				// receives `finishAction`, blocking `onEmptyQueue()`.
				this.initFailed = true;
				log('V2: init failed (initAction returned falsy)', {
					streamingContract: 'v2',
					envelopeId: envelope.id,
				});
				return;
			}
			this.actionId = actionId;
			const contentType = getEnvelopeContentType(envelope);
			const schema = this.editorApi.core.sharedState.currentState()?.schema;
			this.strategy = createToolProcessorChunkStrategyV2(contentType, schema);
			log('V2: initialized streaming action', {
				streamingContract: 'v2',
				streamType: 'update',
				actionId,
				command,
				envelopeId: envelope.id,
			});
		}

		const isComplete = envelope.status === 'complete';
		const rawValue =
			typeof envelope.arguments?.value === 'string' ? (envelope.arguments.value as string) : '';

		if (rawValue) {
			const processed = this.strategy?.replaceSnapshot(rawValue, isComplete) ?? rawValue;
			this.editorApi.aiStreamingOrchestrator?.actions.replaceValue(this.actionId, processed);
			log('V2: replace snapshot', {
				streamingContract: 'v2',
				actionId: this.actionId,
				envelopeId: envelope.id,
				sequence: envelope.sequence,
				status: envelope.status,
			});
		} else if (this.command?.type === 'replaceDoc' && isComplete) {
			// Mirror v1's special case for replaceDoc with an empty value: the
			// orchestrator needs at least one streamValue/replaceValue call so it
			// can dispatch the empty-paragraph fallback rather than no-oping.
			this.editorApi.aiStreamingOrchestrator?.actions.replaceValue(this.actionId, '');
		}

		if (isComplete) {
			this.finish();
		}
	}

	/** Returns the envelope id this processor is bound to, or undefined if not yet initialised. */
	getEnvelopeId(): string | undefined {
		return this.boundEnvelopeId;
	}

	/**
	 * Finish the streaming action.
	 *
	 * When called with an `error` (e.g. from {@link DocumentUpdateCommandProcessor.reset}
	 * on the abort / infinite-loop teardown paths), propagate it through
	 * `finishAction(actionId, error)` so the orchestrator dispatches an error
	 * event rather than treating the last-seen partial snapshot as a
	 * successful tool-call result. This mirrors the v1
	 * `streamCommandQueue.endStreamCommand` error semantics.
	 */
	finish(error?: StreamingError): void {
		if (this.finished) {
			return;
		}
		this.finished = true;
		if (this.actionId) {
			this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId, error);
			log('V2: finished action', {
				streamingContract: 'v2',
				actionId: this.actionId,
				hasError: !!error,
			});
		}
	}

	private boundEnvelopeId: string | undefined;

	/** Bind the envelope id (called by the wrapper on first apply). */
	bindEnvelopeId(id: string): void {
		this.boundEnvelopeId = id;
	}
}

/**
 * Translate a v2 envelope into a {@link StreamingCommand} that the orchestrator
 * understands. Returns `undefined` when required fields are missing (the
 * caller will silently drop the envelope, matching v1 behaviour).
 *
 * Only streamable commands are reachable here — the parser routes everything
 * else to the v1 path before any envelope reaches this function.
 */
function buildCommandFromEnvelope(envelope: ToolCallV2Envelope): StreamingCommand | undefined {
	const args = envelope.arguments ?? {};
	const contentType =
		typeof args.contentType === 'string'
			? (args.contentType as MultiFormatContentType)
			: undefined;
	const localId = typeof args.localId === 'string' ? args.localId : '';
	const noStream = args.noStream === true ? { noStream: true as const } : undefined;

	switch (envelope.name) {
		case 'replaceDoc':
			return {
				type: 'replaceDoc',
				contentType,
				...(noStream ?? {}),
			};
		case 'replaceNode':
		case 'insertNodeBefore':
		case 'insertNodeAfter':
			if (!localId) {
				return undefined;
			}
			return {
				type: envelope.name,
				localId,
				contentType,
				...(noStream ?? {}),
			};
		case 'appendNode':
		case 'appendNodeToEnd':
			return {
				type: envelope.name,
				contentType,
			};
		default:
			return undefined;
	}
}
